"use client";

import React, { useRef, useEffect, useState } from "react";
import { Input } from "./input";
import { cn } from "@/lib/utils";

interface OTPInputProps {
    length?: number;
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
    className?: string;
    autoFocus?: boolean;
}

export function OTPInput({
    length = 6,
    value,
    onChange,
    disabled = false,
    className,
    autoFocus = true,
}: OTPInputProps) {
    const [activeIndex, setActiveIndex] = useState(0);
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    useEffect(() => {
        // Initialize refs array
        inputRefs.current = inputRefs.current.slice(0, length);
    }, [length]);

    useEffect(() => {
        // Auto-focus the first input on mount
        if (autoFocus && inputRefs.current[0] && !disabled) {
            inputRefs.current[0].focus();
        }
    }, [autoFocus, disabled]);

    const handleChange = (index: number, digit: string) => {
        if (disabled) return;

        // Only allow single digits
        if (digit.length > 1) return;

        // Update the value
        const newValue = value.split("");
        newValue[index] = digit;
        const newValueString = newValue.join("");
        onChange(newValueString);

        // Move to next input if digit was entered
        if (digit && index < length - 1) {
            setActiveIndex(index + 1);
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (disabled) return;

        // Handle backspace
        if (e.key === "Backspace") {
            if (value[index]) {
                // Clear current input
                const newValue = value.split("");
                newValue[index] = "";
                onChange(newValue.join(""));
            } else if (index > 0) {
                // Move to previous input and clear it
                setActiveIndex(index - 1);
                inputRefs.current[index - 1]?.focus();
                const newValue = value.split("");
                newValue[index - 1] = "";
                onChange(newValue.join(""));
            }
        }

        // Handle arrow keys
        if (e.key === "ArrowLeft" && index > 0) {
            setActiveIndex(index - 1);
            inputRefs.current[index - 1]?.focus();
        }
        if (e.key === "ArrowRight" && index < length - 1) {
            setActiveIndex(index + 1);
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleFocus = (index: number) => {
        setActiveIndex(index);
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        if (disabled) return;

        e.preventDefault();
        const pastedData = e.clipboardData.getData("text/plain").replace(/\D/g, "");
        const digits = pastedData.slice(0, length).split("");

        // Fill the inputs with pasted digits
        const newValue = value.split("");
        digits.forEach((digit, index) => {
            if (index < length) {
                newValue[index] = digit;
            }
        });
        onChange(newValue.join(""));

        // Focus the next empty input or the last input
        const nextEmptyIndex = newValue.findIndex(digit => !digit);
        const focusIndex = nextEmptyIndex === -1 ? length - 1 : nextEmptyIndex;
        setActiveIndex(focusIndex);
        inputRefs.current[focusIndex]?.focus();
    };

    return (
        <div className={cn("flex gap-2 justify-center", className)}>
            {Array.from({ length }, (_, index) => (
                <Input
                    key={index}
                    ref={(el) => {
                        inputRefs.current[index] = el;
                    }}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={1}
                    value={value[index] || ""}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onFocus={() => handleFocus(index)}
                    onPaste={handlePaste}
                    disabled={disabled}
                    className={cn(
                        "w-12 h-14 text-center text-xl font-semibold",
                        "border-2 transition-all duration-200",
                        activeIndex === index
                            ? "border-blue-500 bg-blue-50"
                            : value[index]
                                ? "border-green-500 bg-green-50"
                                : "border-gray-300 hover:border-gray-400",
                        disabled && "opacity-50 cursor-not-allowed"
                    )}
                    autoComplete="one-time-code"
                />
            ))}
        </div>
    );
} 